import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { adminApi, api, getAdminUser, setAdminSession, clearAdminSession, rupiah, LOGOS } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SeatMap } from "@/components/SeatMap";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Store, Banknote, QrCode, Landmark, Ticket, CheckCircle2, RefreshCw, Lock, UploadCloud, Camera, MapPin, Monitor, Video, Search, UserCheck, ScanLine, X, ArrowLeft, Users, Send, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { DISPLAY_CHANNEL } from "./DisplayPage";

const fmtTimeWIB = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) + " WIB";
  } catch { return ""; }
};

const fmtDateTime = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "-"; }
};

const CHANNELS = {
  umum: { label: "WEBSITE", cls: "bg-[#1E6F8B]/15 text-[#155A73] border-[#1E6F8B]/30" },
  manual: { label: "ORDER MANUAL", cls: "bg-[#B26A1E]/15 text-[#8A3A12] border-[#B26A1E]/30" },
  vip: { label: "TAMU VIP", cls: "bg-[#7A241F]/15 text-[#7A241F] border-[#7A241F]/30" },
  panitia: { label: "PANITIA (LOKASI)", cls: "bg-[#2F703E]/15 text-[#255E33] border-[#2F703E]/30" },
};

function CheckinPanel({ onCheckinDisplay, onFinish }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [popup, setPopup] = useState(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/participants");
      setParticipants(data);
    } catch (err) { if (!silent) toast.error("Gagal memuat data peserta"); }
    if (!silent) setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => load(true), 15000); return () => clearInterval(t); }, [load]);

  const doCheckin = async (o) => {
    if (isCheckinWeb && o.channel === "manual") {
      toast.error("Order Manual harus di-check-in di Counter Tiket Manual (Loket).");
      return;
    }
    setBusyId(o.id);
    try {
      const { data } = await adminApi.post(`/admin/orders/${o.id}/checkin`);
      const info = { ...data, channel: o.channel, session: o.session };
      setPopup(info);
      onCheckinDisplay?.({ name: o.name, channel: o.channel, qty: o.qty, seats: o.seats || [], sessionName: o.session?.name, sessionTime: o.session?.time });
      await load();
    } catch (err) { toast.error("Gagal check-in"); }
    setBusyId(null);
  };

  const finishPopup = () => { setPopup(null); onFinish?.(); };

  const doUncheckin = async (o) => {
    if (!window.confirm(`Batalkan check-in untuk ${o.name}? Peserta akan ditandai BELUM hadir kembali.`)) return;
    setBusyId(o.id);
    try {
      await adminApi.post(`/admin/orders/${o.id}/uncheckin`);
      toast.success(`Check-in ${o.name} dibatalkan`);
      await load();
    } catch (err) { toast.error("Gagal membatalkan check-in"); }
    setBusyId(null);
  };

  const role = getAdminUser()?.role;
  const isCheckinWeb = role === "checkin_web";
  const q = query.trim().toLowerCase();
  const nq = q.replace(/[\s-]/g, "");
  // Cari-dulu: tidak menampilkan seluruh daftar (berat). Hasil muncul hanya saat mengetik.
  const results = q.length < 2 ? [] : participants.filter(
    (o) => o.name.toLowerCase().includes(q) || (o.phone || "").replace(/[\s-]/g, "").includes(nq) || String(o.order_no || "").includes(nq)
  ).slice(0, 12);
  const totalHadir = participants.filter((o) => o.checked_in).length;
  const tix = (o) => o.qty || o.seats?.length || 0;
  const totalTix = participants.reduce((s, o) => s + tix(o), 0);
  const hadirTix = participants.filter((o) => o.checked_in).reduce((s, o) => s + tix(o), 0);
  const sessionStats = Object.values(
    participants.reduce((acc, o) => {
      const key = o.session?.id ?? "none";
      if (!acc[key]) acc[key] = { id: key, name: o.session?.name || "Tanpa Sesi", time: o.session?.time || "", totalTix: 0, hadirTix: 0 };
      acc[key].totalTix += tix(o);
      if (o.checked_in) acc[key].hadirTix += tix(o);
      return acc;
    }, {})
  ).sort((a, b) => String(a.name).localeCompare(String(b.name), "id", { numeric: true }));

  return (
    <div className="max-w-2xl mx-auto px-4 py-5" data-testid="walkin-checkin-panel">
      <div className="rounded-2xl border border-border bg-white p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A6A5E]" />
          <Input data-testid="walkin-checkin-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari nama, nomor HP, atau no. order..." className="pl-9 pr-9 h-11" />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7A6A5E]"><X className="h-4 w-4" /></button>
          )}
        </div>
        <div className="mt-2.5 space-y-2" data-testid="walkin-checkin-stats">
          <p className="text-xs font-semibold text-[#2C1E16] flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-[#2F703E]/10 text-[#255E33] px-2 py-0.5">Total tamu hadir: <b data-testid="walkin-checkin-stat-total">{hadirTix}/{totalTix}</b></span>
            <span className="text-[#7A6A5E]">·</span>
            <span className="text-[#7A6A5E]">{totalHadir}/{participants.length} pesanan</span>
          </p>
          {sessionStats.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="walkin-checkin-session-stats">
              {sessionStats.map((s) => {
                const done = s.totalTix > 0 && s.hadirTix >= s.totalTix;
                return (
                  <span key={s.id} data-testid={`walkin-checkin-stat-session-${s.id}`}
                    className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors",
                      done ? "bg-[#2F703E]/10 border-[#2F703E] ring-2 ring-[#2F703E]/40 text-[#255E33]" : "bg-[#F3E9DD]/60 border-[#B26A1E]/25 text-[#5B4636]")}>
                    <span className="font-semibold text-[#8A3A12]">{s.name}</span>
                    {s.time ? <span className="text-[#7A6A5E]">{s.time}</span> : null}
                    <span className={cn("rounded-full px-1.5 py-0.5 font-bold", done ? "bg-[#2F703E] text-white" : "bg-[#B26A1E] text-white")}>{s.hadirTix}/{s.totalTix}</span>
                    {done && <span className="inline-flex items-center gap-0.5 font-bold text-[#2F703E]"><CheckCircle2 className="h-3 w-3" />PENUH</span>}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#B26A1E]" /></div>
      ) : q.length < 2 ? (
        <div className="text-center py-16" data-testid="walkin-checkin-prompt">
          <Search className="h-8 w-8 text-[#B26A1E]/40 mx-auto mb-3" />
          <p className="text-sm text-[#7A6A5E]">Ketik <b>nomor order</b>, <b>nama</b>, atau <b>no. HP</b> untuk menampilkan peserta.</p>
        </div>
      ) : results.length === 0 ? (
        <p className="text-center text-sm text-[#7A6A5E] py-16">Tidak ada peserta yang cocok dengan "{query}".</p>
      ) : (
        <div className="space-y-3">
          {results.map((o) => {
            const ch = CHANNELS[o.channel] || CHANNELS.umum;
            return (
              <div key={o.id} data-testid={`walkin-checkin-card-${o.id.slice(0, 8)}`}
                className={cn("rounded-2xl border bg-white p-4 shadow-sm", o.checked_in ? "border-[#2F703E]/40 bg-[#2F703E]/5" : "border-border")}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                      <p className="font-semibold text-[#2C1E16] truncate">{o.name}</p>
                      {o.order_no ? <span className="shrink-0 text-[11px] font-bold text-[#7A241F] bg-[#7A241F]/10 px-2 py-0.5 rounded-md">#{o.order_no}</span> : null}
                    </div>
                    <span className={cn("inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-md border", ch.cls)}>{ch.label}</span>
                    <p className="text-xs text-[#7A6A5E] mt-1">{o.session?.name} · {o.session?.time} · {o.qty} tiket</p>
                    {o.channel === "manual" && <p className="mt-1.5 inline-flex text-xs font-bold text-[#8A3A12] bg-[#B26A1E]/15 border border-[#B26A1E]/30 rounded-md px-2 py-1">→ Arahkan ke Counter Tiket Manual</p>}
                  </div>
                  {o.checked_in ? (
                    <span className="shrink-0 inline-flex items-center gap-1 text-xs text-[#255E33] font-medium bg-[#2F703E]/15 px-2 py-1 rounded-full"><CheckCircle2 className="h-3.5 w-3.5" /> Hadir</span>
                  ) : null}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(o.seats || []).map((s) => <span key={s} className="px-3 py-1.5 rounded-lg bg-[#B26A1E]/10 text-[#8A3A12] text-base font-bold">{s}</span>)}
                </div>
                {o.checked_in ? (
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <p className="text-[11px] text-[#7A6A5E]">Check-in: {fmtTimeWIB(o.checked_in_at)}{o.checked_in_by ? <> · oleh <b className="text-[#255E33]">{o.checked_in_by}</b></> : null}</p>
                    <Button onClick={() => doUncheckin(o)} disabled={busyId === o.id} variant="outline"
                      data-testid={`walkin-checkin-undo-btn-${o.id.slice(0, 8)}`}
                      className="shrink-0 h-8 px-2.5 text-xs border-[#7A241F]/40 text-[#7A241F] hover:bg-[#7A241F]/10">
                      {busyId === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />} Batalkan Check-in
                    </Button>
                  </div>
                ) : (isCheckinWeb && o.channel === "manual") ? (
                  <div className="mt-3 rounded-xl bg-[#B26A1E]/10 border-2 border-[#B26A1E]/30 p-3 text-center" data-testid={`walkin-manual-redirect-${o.id.slice(0, 8)}`}>
                    <p className="font-bold text-[#8A3A12]">→ Arahkan ke Counter Tiket Manual</p>
                    <p className="text-[11px] text-[#7A6A5E] mt-0.5">Order manual (rombongan) tidak di-check-in di sini. Tidak ditandai hadir.</p>
                  </div>
                ) : (
                  <Button onClick={() => doCheckin(o)} disabled={busyId === o.id} data-testid={`walkin-checkin-btn-${o.id.slice(0, 8)}`}
                    className="w-full mt-3 h-11 bg-[#B26A1E] hover:bg-[#8A3A12] rounded-xl">
                    {busyId === o.id ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserCheck className="h-4 w-4 mr-1.5" />} Tandai Sudah Datang
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!popup} onOpenChange={(o) => { if (!o) finishPopup(); }}>
        <DialogContent data-testid="walkin-checkin-popup" className="max-w-sm rounded-2xl">
          <DialogHeader>
            <div className="h-11 w-11 rounded-full bg-[#2F703E]/15 flex items-center justify-center mb-2"><Ticket className="h-5 w-5 text-[#2F703E]" /></div>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Peserta Sudah Datang</DialogTitle>
          </DialogHeader>
          {popup && (
            <div className="text-sm space-y-3">
              <p><b>{popup.name}</b> — {popup.session?.name} · {popup.session?.time}</p>
              {popup.channel === "manual" ? (
                <div className="rounded-lg bg-[#7A241F]/[0.06] border-2 border-[#7A241F]/30 p-4 text-center">
                  <p className="text-[#8A3A12] font-medium mb-1">Order Manual (Rombongan)</p>
                  <p className="font-serif-display text-2xl text-[#7A241F] leading-tight mb-2">Arahkan ke<br />COUNTER TIKET MANUAL</p>
                  <p className="text-xs text-[#7A6A5E]">Tiket fisik ({popup.seats?.length || popup.qty} tiket) sudah disiapkan di stand manual.</p>
                </div>
              ) : (
                <div className="rounded-lg bg-[#B26A1E]/10 p-4">
                  <p className="text-[#8A3A12] font-medium mb-2">Serahkan tiket:</p>
                  <p className="font-serif-display text-2xl text-[#7A241F] mb-2">{popup.session?.name?.toUpperCase()} · {popup.session?.time}</p>
                  <p className="text-xs text-[#7A6A5E] mb-1">Nomor kursi <b className="text-[#8A3A12]">({popup.seats?.length} tiket)</b>:</p>
                  <div className="flex flex-wrap gap-2">{(popup.seats || []).map((s) => <span key={s} className="px-3 py-1.5 rounded-md bg-white text-[#8A3A12] font-bold border border-[#B26A1E]/30">{s}</span>)}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={finishPopup} className="bg-[#B26A1E] hover:bg-[#8A3A12] w-full h-11" data-testid="walkin-checkin-ok">Selesai &amp; Kembali ke Menu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1000;
        let { width, height } = img;
        if (width > max || height > max) {
          const r = Math.min(max / width, max / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const SESSIONS = [
  { id: 1, name: "Sesi 1", time: "09.30–11.30 WIB" },
  { id: 2, name: "Sesi 2", time: "12.00–14.00 WIB" },
  { id: 3, name: "Sesi 3", time: "14.30–16.30 WIB" },
  { id: 4, name: "Sesi 4", time: "17.00–19.00 WIB" },
  { id: 5, name: "Sesi 5 (Tambahan)", time: "15.00–17.00 WIB" },
];
const PAY = [
  { k: "qris", t: "QRIS", icon: QrCode },
  { k: "transfer", t: "Transfer", icon: Landmark },
  { k: "cash", t: "Cash", icon: Banknote },
];
const REF_COST = 60000;

function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { username, password });
      if (!["admin", "superadmin", "seller", "loket", "checkin_web", "checkin"].includes(data.user.role)) {
        toast.error("Akun ini tidak punya akses panitia");
        setLoading(false);
        return;
      }
      setAdminSession(data.user);
      onLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login gagal");
    }
    setLoading(false);
  };
  return (
    <div className="min-h-screen bg-[#2F703E] flex flex-col items-center justify-center px-6">
      <img src={LOGOS.kbi} alt="KBI" className="h-14 mb-6 bg-white/95 rounded-lg p-2" />
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-7 shadow-xl">
        <div className="h-12 w-12 rounded-full bg-[#2F703E]/10 flex items-center justify-center mb-3">
          <Store className="h-6 w-6 text-[#2F703E]" />
        </div>
        <h1 className="font-serif-display text-2xl text-[#2F703E]">Jual Tiket di Tempat</h1>
        <p className="text-sm text-[#7A6A5E] mb-5">Masuk dengan akun panitia (Admin).</p>
        <Label htmlFor="u">Username</Label>
        <Input id="u" value={username} autoCapitalize="none" onChange={(e) => setUsername(e.target.value)}
          className="mt-1.5 mb-3" data-testid="walkin-login-username" placeholder="username" />
        <Label htmlFor="p">Password</Label>
        <Input id="p" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5" data-testid="walkin-login-password" placeholder="••••••••" />
        <Button type="submit" disabled={loading} data-testid="walkin-login-btn"
          className="w-full mt-5 bg-[#B26A1E] hover:bg-[#8A3A12] rounded-full h-11">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Masuk
        </Button>
      </form>
    </div>
  );
}

export default function WalkinPage({ preorder = false }) {
  const [authed, setAuthed] = useState(!!getAdminUser());
  const [user, setUser] = useState(getAdminUser());
  const [panitiaMode, setPanitiaMode] = useState("menu"); // menu | order | checkin
  const role = user?.role;
  const canSell = ["superadmin", "admin", "seller", "loket"].includes(role);
  const canCheckin = !preorder && ["superadmin", "admin", "loket", "checkin", "checkin_web"].includes(role);
  const isPreorder = preorder || role === "seller"; // /preorder ATAU role seller = jual tanpa check-in
  const [checkinView, setCheckinView] = useState(null); // data peserta yg baru di-check-in (utk layar monitor)
  const [sessionId, setSessionId] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [loadingMap, setLoadingMap] = useState(true);
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState(() => localStorage.getItem("walkin_location") || "");
  const [locHistory, setLocHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("walkin_locations") || "[]"); } catch { return []; }
  });
  const [method, setMethod] = useState("");
  const [amountText, setAmountText] = useState("");
  const [preorderPaid, setPreorderPaid] = useState(false); // /preorder: sudah berdana?
  const [ucode, setUcode] = useState(0); // kode unik QRIS/Transfer
  const [proof, setProof] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [transfer, setTransfer] = useState(null);
  const [walkinSessions, setWalkinSessions] = useState({});
  const [displayMode, setDisplayMode] = useState("welcome"); // welcome | selecting | paying
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [payStep, setPayStep] = useState(null); // null | "pay"
  const [pendingOrder, setPendingOrder] = useState(null);
  const pollRef = useRef(null);
  const chanRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const loadEvent = useCallback(async () => {
    try {
      const { data } = await api.get("/event");
      setTransfer(data.transfer);
      const map = {};
      (data.sessions || []).forEach((s) => { map[s.id] = !!s.walkin_open; });
      setWalkinSessions(map);
    } catch (e) { console.error("Gagal memuat status sesi:", e); }
  }, []);

  useEffect(() => { loadEvent(); }, [loadEvent]);

  const loadMap = useCallback(async (sid, showSpinner = false) => {
    if (showSpinner) setLoadingMap(true);
    try {
      const { data } = await api.get(`/sessions/${sid}/seats`);
      setMapData(data);
      // Auto-lepas kursi yang BARU SAJA diambil penjual lain (anti-rebutan kursi)
      const taken = new Set();
      (data.rows || []).forEach((r) => (r.blocks || []).forEach((b) => b.forEach((s) => {
        if (s.status === "booked" || s.status === "reserved") taken.add(s.label);
      })));
      setSelected((prev) => {
        const removed = prev.filter((x) => taken.has(x));
        if (removed.length) {
          toast.error(`Kursi ${removed.join(", ")} baru saja diambil penjual lain — otomatis dilepas dari pilihan Anda.`);
          return prev.filter((x) => !taken.has(x));
        }
        return prev;
      });
    } catch (e) { console.error("Gagal memuat peta kursi:", e); }
    if (showSpinner) setLoadingMap(false);
  }, []);

  useEffect(() => {
    if (!authed) return;
    setSelected([]);
    if (!sessionId) { setMapData(null); if (pollRef.current) clearInterval(pollRef.current); return; }
    loadMap(sessionId, true);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => { loadMap(sessionId, false); loadEvent(); }, 5000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [authed, sessionId, loadMap, loadEvent]);

  // ---- Sinkron Layar Monitor Pelanggan (BroadcastChannel, 1 laptop 2 jendela) ----
  const displayPayload = useRef({ mode: "idle" });
  useEffect(() => {
    const ch = new BroadcastChannel(DISPLAY_CHANNEL);
    chanRef.current = ch;
    ch.onmessage = (e) => {
      if (e.data?.type === "hello") ch.postMessage({ type: "state", payload: displayPayload.current });
    };
    return () => ch.close();
  }, []);

  useEffect(() => {
    if (!authed) return;
    const s = SESSIONS.find((x) => x.id === sessionId);
    let payload;
    if (panitiaMode === "checkin") {
      payload = checkinView ? { mode: "checkin", ...checkinView } : { mode: "idle" };
    } else if (result) {
      payload = { mode: "done", result, sessionName: s?.name, sessionTime: s?.time };
    } else {
      const paying = displayMode === "paying" && pendingOrder;
      payload = {
        mode: displayMode === "welcome" ? "idle" : displayMode,
        sessionId, sessionName: s?.name, sessionTime: s?.time,
        rows: mapData?.rows || null, couples: mapData?.couples || {},
        selected, method,
        buyerName: name, buyerPhone: phone,
        amount: paying ? pendingOrder.total_amount : (parseInt(amountText || "0", 10) || 0),
        orderNo: paying ? pendingOrder.order_no : null,
        qty: paying ? (pendingOrder.seats?.length || pendingOrder.qty) : null,
        transfer,
        remaining: mapData ? (mapData.capacity - mapData.booked) : null,
      };
    }
    displayPayload.current = payload;
    chanRef.current?.postMessage({ type: "state", payload });
  }, [authed, panitiaMode, checkinView, result, displayMode, sessionId, mapData, selected, method, amountText, transfer, pendingOrder, name, phone]);

  const openMonitor = () => window.open("/display", "kbi_monitor", "width=1280,height=800");

  const openWebcam = async () => {
    setWebcamOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
    } catch (e) {
      toast.error("Tidak bisa akses webcam. Izinkan kamera di browser, atau pakai Upload File.");
      setWebcamOpen(false);
    }
  };
  const stopWebcam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setWebcamOpen(false);
  };
  const snapWebcam = () => {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    const max = 1400;
    let w = v.videoWidth || 1280, h = v.videoHeight || 720;
    if (w > max || h > max) { const r = Math.min(max / w, max / h); w = Math.round(w * r); h = Math.round(h * r); }
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(v, 0, 0, w, h);
    setProof(canvas.toDataURL("image/jpeg", 0.82));
    stopWebcam();
  };

  if (!authed) return <Login onLogin={() => { setUser(getAdminUser()); setAuthed(true); }} />;

  const isStaff = ["admin", "superadmin", "seller"].includes(user?.role);
  if (!isStaff) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center px-6 text-center">
        <p className="text-[#7A6A5E]">Akun Anda tidak diizinkan menjual tiket di tempat.</p>
        <button onClick={() => { clearAdminSession(); setAuthed(false); }} className="mt-3 text-xs text-[#EF4444] underline">Keluar</button>
      </div>
    );
  }

  const toggle = (label, partner = null) => {
    setDisplayMode("selecting");
    setSelected((p) => {
      const pair = partner ? [label, partner] : [label];
      if (p.includes(label)) return p.filter((x) => !pair.includes(x));
      return [...p.filter((x) => !pair.includes(x)), ...pair];
    });
  };

  const amount = parseInt(amountText || "0", 10) || 0;
  const refTotal = selected.length * REF_COST;
  const total = amount;

  const doCreate = async (proofImg) => {
    const { data } = await adminApi.post("/admin/walkin", {
      name, phone, session_id: sessionId, seats: selected, payment_method: method, amount,
      proof_image: proofImg || null, location: location.trim(), preorder: isPreorder,
    });
    localStorage.setItem("walkin_location", location.trim());
    const loc = location.trim();
    const nextHist = [loc, ...locHistory.filter((l) => l.toLowerCase() !== loc.toLowerCase())].slice(0, 12);
    setLocHistory(nextHist);
    localStorage.setItem("walkin_locations", JSON.stringify(nextHist));
    return data;
  };

  const clearForm = () => { setName(""); setPhone(""); setSelected([]); setAmountText(""); setProof(null); setMethod(""); setUcode(0); };

  const onProofPreorder = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setProof(null); return; }
    if (!f.type.startsWith("image/")) { toast.error("File harus berupa gambar"); return; }
    compressImage(f).then(setProof).catch(() => toast.error("Gagal memproses gambar"));
  };

  const submitPreorder = async () => {
    if (!name.trim()) { toast.error("Isi nama pembeli"); return; }
    if (!phone.trim()) { toast.error("Isi No HP pembeli — wajib untuk kirim e-ticket via WhatsApp"); return; }
    if (selected.length === 0) { toast.error("Pilih minimal 1 kursi"); return; }
    if (preorderPaid && amount <= 0) { toast.error("Isi nominal Dana Paramita"); return; }
    if (preorderPaid && !method) { toast.error("Pilih metode pembayaran dulu"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/preorder", {
        name, phone, session_id: sessionId, seats: selected,
        paid: preorderPaid, amount: preorderPaid ? amount : 0,
        payment_method: method, unique_code: (preorderPaid && method !== "cash") ? ucode : 0,
        proof_image: preorderPaid ? (proof || null) : null,
        location: location.trim(),
      });
      setResult(data); clearForm(); setPreorderPaid(false); loadMap(sessionId, false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat pre-order");
      loadMap(sessionId, false);
    }
    setBusy(false);
  };

  // E-Ticket (template sama dgn Order Manual di panel admin) — jalan di Windows/Android/iPhone
  const saveTicketImage = async (o) => {
    if (!o) return;
    const session = SESSIONS.find((s) => s.id === o.session_id);
    const paid = !!o.paid;
    const by = o.created_by || o.sold_by || "-";
    const W = 820;
    const seats = o.seats || [];
    const _m = document.createElement("canvas").getContext("2d");
    _m.font = "bold 25px Arial";
    const seatMaxW = 470;
    const seatLines = [];
    let _cur = "";
    seats.forEach((s) => {
      const t = _cur ? _cur + ", " + s : s;
      if (_m.measureText(t).width > seatMaxW && _cur) { seatLines.push(_cur); _cur = s; } else _cur = t;
    });
    if (_cur) seatLines.push(_cur);
    if (seatLines.length === 0) seatLines.push("-");
    const wrapVal = (text, maxW) => {
      const words = String(text || "-").split(" ");
      const lines = []; let ln = "";
      words.forEach((w) => {
        const t = ln ? ln + " " + w : w;
        if (_m.measureText(t).width > maxW && ln) { lines.push(ln); ln = w; } else ln = t;
      });
      if (ln) lines.push(ln);
      return lines.length ? lines : ["-"];
    };
    const nameLines = wrapVal(o.name, 470);
    const H = 1180 + (seatLines.length - 1) * 38 + (nameLines.length - 1) * 38;
    const draw = (logoImg) => {
      const c = document.createElement("canvas"); c.width = W; c.height = H;
      const x = c.getContext("2d");
      x.fillStyle = "#7A241F"; x.fillRect(0, 0, W, H);
      x.fillStyle = "#FDFBF7"; x.fillRect(30, 30, W - 60, H - 60);
      x.fillStyle = "#B26A1E"; x.fillRect(30, 30, W - 60, 12);
      const cx = W / 2; x.textAlign = "center";
      let top = 130;
      if (logoImg) {
        try {
          const maxH = 92, maxW = 300;
          let lw = logoImg.naturalWidth || logoImg.width || 1, lh = logoImg.naturalHeight || logoImg.height || 1;
          const r = Math.min(maxW / lw, maxH / lh);
          lw = lw * r; lh = lh * r;
          x.drawImage(logoImg, cx - lw / 2, 52, lw, lh);
          top = 52 + lh + 40;
        } catch (e) { /* skip logo */ }
      }
      x.fillStyle = "#B26A1E"; x.font = "bold 26px Georgia"; x.fillText("E-TICKET · FILM DOKUMENTER", cx, top);
      x.fillStyle = "#7A241F"; x.font = "bold 38px Georgia"; x.fillText("ASHIN JINARAKKHITA", cx, top + 48);
      const wrap = (text, maxW) => {
        const words = text.split(" "); const lines = []; let line = "";
        words.forEach((w) => {
          const t = line ? line + " " + w : w;
          if (x.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t;
        });
        if (line) lines.push(line);
        return lines;
      };
      x.fillStyle = "#8A6A3E"; x.font = "italic 17px Georgia";
      let sy = top + 80;
      wrap("Jejak Langkah Sang Pelopor Membangkitkan Kembali Dharma di Nusantara.", W - 200).forEach((ln) => { x.fillText(ln, cx, sy); sy += 24; });
      x.fillStyle = "#5B4636"; x.font = "18px Arial";
      x.fillText("Minggu, 13 September 2026", cx, sy + 10);
      x.fillText("CGV Grand Batam Mall", cx, sy + 36);
      let y0 = sy + 68;
      x.strokeStyle = "#B26A1E"; x.setLineDash([8, 6]); x.beginPath(); x.moveTo(70, y0); x.lineTo(W - 70, y0); x.stroke(); x.setLineDash([]);
      const rows = [
        ["No. Order", `#${o.order_no}`],
        ["Nama", null],
        ["Sesi", `${session?.name || "-"} · ${session?.time || ""}`],
        ["Nomor Kursi", null],
        ["Jumlah", `${o.qty || seats.length} tiket`],
        ["Tgl & Jam Pesan", fmtDateTime(o.created_at)],
        ["Diinput oleh", by],
      ];
      if (paid) rows.push(["Status Pembayaran", rupiah(o.total_amount)]);
      let y = y0 + 54; x.textAlign = "left";
      rows.forEach(([k, v]) => {
        x.fillStyle = "#7A6A5E"; x.font = "20px Arial"; x.fillText(k, 80, y);
        if (k === "Nomor Kursi" || k === "Nama") {
          const lines = k === "Nama" ? nameLines : seatLines;
          x.fillStyle = "#2C1E16"; x.font = "bold 25px Arial"; x.textAlign = "right";
          lines.forEach((ln, i) => { x.fillText(ln, W - 80, y + i * 38); });
          x.textAlign = "left"; y += 60 + (lines.length - 1) * 38;
        } else {
          x.fillStyle = k === "Status Pembayaran" ? (paid ? "#255E33" : "#8A3A12") : "#2C1E16";
          x.font = "bold 25px Arial"; x.textAlign = "right"; x.fillText(String(v).slice(0, 40), W - 80, y);
          x.textAlign = "left"; y += 60;
        }
      });
      x.textAlign = "center";
      x.fillStyle = "#7A241F"; x.font = "bold 30px Georgia"; x.fillText(`#${o.order_no}`, cx, y + 28);
      x.fillStyle = "#8A3A12"; x.font = "bold 21px Arial";
      x.fillText("Tunjukkan e-ticket ini kepada petugas di lokasi", cx, H - 108);
      x.fillText("untuk menukar tiket fisik (hardcopy) asli.", cx, H - 80);
      x.fillStyle = "#B26A1E"; x.font = "16px Arial";
      x.fillText("Keluarga Buddhayana Indonesia Prov. Kepulauan Riau", cx, H - 48);
      return c;
    };
    try {
      let logo = null;
      await new Promise((res) => { const img = new Image(); img.crossOrigin = "anonymous"; img.onload = () => { logo = img; res(); }; img.onerror = () => res(); img.src = LOGOS.kbi; });
      let canvas;
      try { canvas = draw(logo); } catch (e) { canvas = draw(null); }
      const fileName = `e-ticket-${o.order_no}.png`;
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      const isMobile = isIOS || /Android/i.test(navigator.userAgent);
      if (isMobile && blob && navigator.canShare) {
        try {
          const file = new File([blob], fileName, { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: `E-Ticket #${o.order_no}` });
            toast.success("E-ticket siap dibagikan/disimpan");
            return;
          }
        } catch (err) { if (err?.name === "AbortError") return; }
      }
      const url = blob ? URL.createObjectURL(blob) : canvas.toDataURL("image/png");
      if (isIOS) {
        window.open(url, "_blank");
        toast.info("Tekan lama gambar lalu pilih \u201CSimpan ke Foto\u201D", { duration: 6000 });
        return;
      }
      const a = document.createElement("a"); a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      if (blob) setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("E-ticket tersimpan sebagai gambar");
    } catch (e) { console.error("save ticket:", e); toast.error("Gagal membuat e-ticket"); }
  };

  const startPayment = async () => {
    if (!walkinSessions[sessionId]) { toast.error("Sesi ini belum dibuka untuk panitia (lokasi)"); return; }
    if (!name.trim()) { toast.error("Isi nama pembeli"); return; }
    if (selected.length === 0) { toast.error("Pilih minimal 1 kursi"); return; }
    if (amount <= 0) { toast.error("Isi nominal dana sukarela"); return; }
    if (!method) { toast.error("Pilih metode pembayaran dulu"); return; }
    if (!location.trim()) { toast.error("Isi lokasi penjualan"); return; }
    setBusy(true);
    try {
      const data = await doCreate(null);
      if (method === "cash") {
        setResult(data); clearForm(); loadMap(sessionId, false);
      } else {
        setProof(null); setPendingOrder(data); setDisplayMode("paying"); setPayStep("pay");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat tiket");
      loadMap(sessionId, false);
    }
    setBusy(false);
  };

  const confirmPayment = async () => {
    if (!pendingOrder || !proof) { toast.error("Foto struk dulu"); return; }
    setBusy(true);
    try {
      await adminApi.post(`/admin/walkin/${pendingOrder.id}/proof`, { proof_image: proof });
      setPayStep(null); setResult(pendingOrder); setPendingOrder(null);
      clearForm(); loadMap(sessionId, false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal simpan bukti");
    }
    setBusy(false);
  };

  const cancelPayment = async () => {
    if (busy) return;
    const po = pendingOrder;
    setPayStep(null); setDisplayMode("selecting"); setPendingOrder(null); setProof(null);
    if (po) { try { await adminApi.delete(`/admin/walkin/${po.id}`); } catch (err) { console.error("Gagal batalkan walk-in:", err); } loadMap(sessionId, false); }
  };

  // Selesai jual tiket → kembali ke menu awal (Check-in / Jual Tiket)
  const finishSaleToMenu = () => {
    setResult(null); setDisplayMode("welcome"); setSessionId(null); clearForm();
    setPreorderPaid(false);
    if (!isPreorder) setPanitiaMode("menu");
  };

  const remaining = mapData ? (mapData.capacity - mapData.booked) : null;
  const sold = mapData && remaining <= 0;
  const sessionWalkinOpen = isPreorder ? !!sessionId : walkinSessions[sessionId];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-[#2F703E] text-white shadow-md">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {panitiaMode !== "menu" && (
              <button onClick={() => { setPanitiaMode("menu"); setSessionId(null); setDisplayMode("welcome"); setResult(null); setCheckinView(null); }}
                data-testid="walkin-back-menu" className="mr-1 inline-flex items-center gap-1 text-xs font-semibold bg-white/15 hover:bg-white/25 rounded-full px-2.5 py-1.5 transition-colors">
                <ArrowLeft className="h-4 w-4" /> Menu
              </button>
            )}
            <img src={LOGOS.kbi} alt="KBI" className="h-8 bg-white/95 rounded p-1" />
            <div>
              <p className="text-sm font-semibold leading-tight flex items-center gap-1.5">
                {panitiaMode === "checkin" ? <><ScanLine className="h-4 w-4" /> Check-in Peserta</> : preorder ? <><Store className="h-4 w-4" /> Pre-Order Tiket</> : <><Store className="h-4 w-4" /> Jual Tiket di Tempat</>}
              </p>
              <p className="text-[11px] text-white/70 leading-tight">Petugas: {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={openMonitor} data-testid="walkin-open-monitor"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 transition-colors">
              <Monitor className="h-4 w-4" /> Buka Layar Monitor
            </button>
            <button onClick={() => { adminApi.post("/admin/logout").catch(() => {}); clearAdminSession(); setAuthed(false); }} data-testid="walkin-logout" className="text-xs text-white/80 underline">Keluar</button>
          </div>
        </div>
      </div>

      {/* Menu utama panitia — muat penuh 1 layar tanpa scroll */}
      {panitiaMode === "menu" && (
        <div className="h-[calc(100vh-58px)] overflow-hidden flex flex-col justify-center max-w-4xl mx-auto w-full px-4 py-4" data-testid="walkin-menu">
          <div className="rounded-2xl overflow-hidden border border-[#B26A1E]/30 bg-gradient-to-br from-[#2F703E] to-[#255E33] text-white px-6 py-5 text-center shadow-lg">
            <img src={LOGOS.kbi} alt="KBI" className="h-12 mx-auto bg-white/95 rounded-lg p-1.5 mb-2" />
            <p className="text-[10px] tracking-widest text-white/70 uppercase">Panitia · Hari Acara</p>
            <h1 className="font-serif-display text-2xl sm:text-3xl leading-tight">Film Dokumenter <span className="text-[#E4C57E]">Ashin Jinarakkhita</span></h1>
            <p className="text-xs text-white/80 mt-1">Minggu, 13 September 2026 · CGV Grand Batam</p>
          </div>
          <p className="text-center text-sm text-[#7A6A5E] mt-4 mb-3">{preorder ? "Mode PRE-ORDER — jual tiket, peserta check-in di hari-H:" : "Pilih tugas Anda:"}</p>
          <div className={cn("grid gap-3 sm:gap-4", canSell && canCheckin && !preorder ? "grid-cols-2" : "grid-cols-1 max-w-md mx-auto w-full")}>
            {canSell && (
            <button onClick={() => { setPanitiaMode("order"); setDisplayMode("welcome"); }} data-testid="walkin-menu-order"
              className="group rounded-2xl border-2 border-[#B26A1E]/40 bg-white px-4 py-6 text-center hover:border-[#B26A1E] hover:bg-[#B26A1E]/5 transition-colors shadow-sm">
              <span className="h-14 w-14 mx-auto rounded-2xl bg-[#B26A1E]/10 group-hover:bg-[#B26A1E]/20 flex items-center justify-center mb-3 transition-colors">
                <Store className="h-7 w-7 text-[#B26A1E]" />
              </span>
              <p className="font-serif-display text-xl sm:text-2xl text-[#7A241F]">{preorder ? "Pesan Tiket (Pre-Order)" : "Pesan Tiket"}</p>
              <p className="text-xs sm:text-sm text-[#7A6A5E] mt-1">{preorder ? "Jual tiket pre-order — TANPA check-in. Kirim e-ticket via WhatsApp." : "Jual tiket di tempat: pilih sesi, kursi, bayar."}</p>
            </button>
            )}
            {canCheckin && !preorder && (
            <button onClick={() => setPanitiaMode("checkin")} data-testid="walkin-menu-checkin"
              className="group rounded-2xl border-2 border-[#2F703E]/40 bg-white px-4 py-6 text-center hover:border-[#2F703E] hover:bg-[#2F703E]/5 transition-colors shadow-sm">
              <span className="h-14 w-14 mx-auto rounded-2xl bg-[#2F703E]/10 group-hover:bg-[#2F703E]/20 flex items-center justify-center mb-3 transition-colors">
                <ScanLine className="h-7 w-7 text-[#2F703E]" />
              </span>
              <p className="font-serif-display text-xl sm:text-2xl text-[#7A241F]">Check In</p>
              <p className="text-xs sm:text-sm text-[#7A6A5E] mt-1">Catat kehadiran peserta & serahkan tiket.</p>
            </button>
            )}
          </div>
        </div>
      )}

      {panitiaMode === "checkin" && !preorder && (
        <CheckinPanel
          onCheckinDisplay={(v) => setCheckinView(v)}
          onFinish={() => { setCheckinView(null); setPanitiaMode("menu"); }}
        />
      )}

      {panitiaMode === "order" && (
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-5 grid lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Seat map — the big monitor area */}
        <div className="lg:col-span-2 rounded-2xl border border-border bg-white p-3 sm:p-5 min-w-0 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex flex-wrap gap-2">
              {SESSIONS.map((s) => {
                const isWalkinOpen = walkinSessions[s.id];
                return (
                  <button key={s.id} data-testid={`walkin-session-${s.id}`} onClick={() => { setSessionId(s.id); setDisplayMode("selecting"); }}
                    className={cn("px-3 py-1.5 rounded-full text-sm font-medium border transition-colors relative",
                      sessionId === s.id ? "bg-[#B26A1E] text-white border-[#B26A1E]"
                        : isWalkinOpen ? "bg-white text-[#7A6A5E] border-border hover:border-[#B26A1E]/50"
                          : "bg-muted/50 text-[#9CA3AF] border-border")}>
                    {s.name} · {s.time}
                    {!isPreorder && (
                    <span className={cn("ml-1.5 text-[10px] font-semibold", isWalkinOpen ? (sessionId === s.id ? "text-white/90" : "text-[#255E33]") : "text-[#B26A1E]")}>
                      {isWalkinOpen ? "• BUKA" : "• TUTUP"}
                    </span>
                    )}
                  </button>
                );
              })}
            </div>
            <button onClick={() => loadMap(sessionId, true)} className="inline-flex items-center gap-1 text-xs text-[#7A6A5E] hover:text-[#B26A1E]">
              <RefreshCw className="h-3.5 w-3.5" /> Perbarui
            </button>
          </div>

          {mapData && sessionId && sessionWalkinOpen && (
            <p className="text-sm mb-3" data-testid="walkin-remaining">
              {sold ? <span className="font-semibold text-[#EF4444]">Kursi sesi ini habis terjual</span>
                : <>Sisa <b className="text-[#255E33] text-lg">{remaining}</b> kursi · {mapData.booked}/{mapData.capacity} terisi</>}
            </p>
          )}

          {!sessionId ? (
            <div className="rounded-xl border-2 border-dashed border-[#B26A1E]/40 bg-[#F3E9DD]/40 p-10 text-center" data-testid="walkin-pick-session">
              <Store className="h-12 w-12 text-[#B26A1E] mx-auto mb-3" />
              <p className="font-serif-display text-2xl text-[#7A241F]">Pilih Sesi Dulu</p>
              <p className="text-sm text-[#7A6A5E] mt-1.5 max-w-sm mx-auto">Tekan salah satu <b>tombol sesi</b> di atas untuk menampilkan peta kursi.</p>
            </div>
          ) : !sessionWalkinOpen ? (
            <div className="rounded-xl border border-[#B26A1E]/40 bg-[#F3E9DD]/50 p-8 text-center" data-testid="walkin-session-closed">
              <Store className="h-10 w-10 text-[#B26A1E] mx-auto mb-3" />
              <p className="font-semibold text-[#7A241F]">Sesi ini belum dibuka untuk penjualan panitia</p>
              <p className="text-sm text-[#7A6A5E] mt-1 max-w-sm mx-auto">
                Minta <b>Super Admin</b> membuka sesi ini lewat panel admin
                (Verifikasi Pembayaran → Buka/Tutup Penjualan per Sesi → saklar <b>Panitia (Lokasi)</b>), lalu pilih sesi ini lagi.
              </p>
            </div>
          ) : loadingMap ? (
            <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#B26A1E]" /></div>
          ) : mapData ? (
            <div className="overflow-x-auto -mx-1 px-1">
              <SeatMap rows={mapData.rows} selected={selected} onToggle={toggle} couples={mapData.couples || {}} allowDisability
                aisleRow={mapData.aisle_row} doorRow={mapData.door_row} reverse={mapData.reverse} />
            </div>
          ) : (
            <p className="text-center text-sm text-[#7A6A5E] py-16">Gagal memuat peta kursi.</p>
          )}
        </div>

        {/* Order form */}
        <div className="rounded-2xl border border-border bg-white p-5 h-fit lg:sticky lg:top-20">
          <h2 className="font-serif-display text-xl text-[#7A241F] mb-3">Data Pembeli</h2>
          <Label htmlFor="wn">Nama</Label>
          <Input id="wn" data-testid="walkin-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama pembeli" className="mt-1.5 mb-3" />
          <Label htmlFor="wp">No. HP {isPreorder ? <span className="text-[#EF4444]">* (untuk kirim e-ticket WA)</span> : <span className="text-[#9CA3AF]">(opsional)</span>}</Label>
          <Input id="wp" data-testid="walkin-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="08xxxx" className="mt-1.5 mb-3" />

          <Label htmlFor="wl">Lokasi Penjualan <span className="text-[#EF4444]">*</span></Label>
          <div className="relative mt-1.5 mb-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B26A1E]" />
            <Input id="wl" data-testid="walkin-location" value={location} onChange={(e) => setLocation(e.target.value)}
              list="walkin-loc-list" autoComplete="off"
              placeholder="mis. Vihara Buddhayana" className="pl-9" />
            <datalist id="walkin-loc-list">
              {locHistory.map((l) => <option key={l} value={l} />)}
            </datalist>
          </div>
          {locHistory.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="walkin-loc-suggestions">
              {locHistory.slice(0, 5).map((l) => (
                <button key={l} type="button" onClick={() => setLocation(l)}
                  className={cn("text-[11px] rounded-full px-2.5 py-1 border transition-colors",
                    location.trim().toLowerCase() === l.toLowerCase()
                      ? "bg-[#B26A1E] text-white border-[#B26A1E]"
                      : "bg-white text-[#7A6A5E] border-border hover:border-[#B26A1E]")}>
                  {l}
                </button>
              ))}
            </div>
          )}

          {isPreorder ? (
            <>
              <Label>Status Dana Paramita</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5 mb-3">
                <button type="button" data-testid="preorder-belum" onClick={() => setPreorderPaid(false)}
                  className={cn("rounded-xl border px-3 py-2.5 text-sm font-semibold leading-tight transition-colors",
                    !preorderPaid ? "border-[#B26A1E] bg-[#B26A1E]/10 text-[#8A3A12] ring-2 ring-[#B26A1E]/30" : "border-border bg-white text-[#7A6A5E] hover:border-[#B26A1E]/50")}>
                  Belum Berdana<span className="block text-[10px] font-normal">nyusul via link</span>
                </button>
                <button type="button" data-testid="preorder-sudah" onClick={() => setPreorderPaid(true)}
                  className={cn("rounded-xl border px-3 py-2.5 text-sm font-semibold leading-tight transition-colors",
                    preorderPaid ? "border-[#2F703E] bg-[#2F703E]/10 text-[#255E33] ring-2 ring-[#2F703E]/30" : "border-border bg-white text-[#7A6A5E] hover:border-[#2F703E]/50")}>
                  Sudah Berdana<span className="block text-[10px] font-normal">isi nominal & bukti</span>
                </button>
              </div>

              <div className="rounded-lg bg-muted/40 p-3 mb-3">
                <p className="text-xs text-[#7A6A5E] mb-1">Kursi dipilih ({selected.length})</p>
                <div className="flex flex-wrap gap-1.5 min-h-[28px]" data-testid="walkin-selected-seats">
                  {selected.length === 0 ? <span className="text-xs text-[#9CA3AF]">Belum ada kursi dipilih</span>
                    : selected.map((s) => <span key={s} className="px-2.5 py-1 rounded-md bg-[#B26A1E]/10 text-[#8A3A12] text-sm font-bold">{s}</span>)}
                </div>
              </div>

              {preorderPaid ? (
                <>
                  <div className="rounded-lg border border-[#B26A1E]/30 bg-[#F3E9DD]/50 p-3 mb-3">
                    <p className="text-xs font-semibold text-[#7A241F] mb-2">Nominal Dana Paramita</p>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#7A6A5E]">Rp</span>
                      <Input data-testid="preorder-amount" inputMode="numeric"
                        value={amount ? amount.toLocaleString("id-ID") : ""}
                        onChange={(e) => setAmountText(e.target.value.replace(/[^0-9]/g, ""))}
                        placeholder="0" className="pl-9 h-11 text-base font-semibold bg-white" />
                    </div>
                  </div>
                  <Label>Metode Pembayaran</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5 mb-3">
                    {PAY.map((m) => {
                      const Icon = m.icon; const active = method === m.k;
                      return (
                        <button type="button" key={m.k} data-testid={`preorder-pay-${m.k}`} onClick={() => { setMethod(m.k); setUcode(m.k === "cash" ? 0 : Math.floor(100 + Math.random() * 900)); }}
                          className={cn("rounded-xl border p-2.5 text-center transition-colors",
                            active ? "border-[#B26A1E] bg-[#B26A1E]/5 ring-2 ring-[#B26A1E]/30" : "border-border hover:border-[#B26A1E]/50")}>
                          <Icon className={cn("h-5 w-5 mx-auto mb-0.5", active ? "text-[#B26A1E]" : "text-[#7A6A5E]")} />
                          <span className="text-xs font-medium text-[#2C1E16]">{m.t}</span>
                        </button>
                      );
                    })}
                  </div>
                  {method && method !== "cash" && (
                    <div className="rounded-lg border border-[#B26A1E]/30 bg-white p-3 mb-3">
                      {amount > 0 && (
                        <div className="mb-3 rounded-lg bg-[#7A241F]/10 border border-[#7A241F]/30 p-2.5" data-testid="preorder-total-unik">
                          <p className="text-[11px] text-[#7A6A5E]">Total transfer (termasuk kode unik <b>{ucode}</b>):</p>
                          <p className="text-xl font-bold text-[#7A241F]">{rupiah(amount + ucode)}</p>
                          <p className="text-[10px] text-[#7A6A5E]">Minta pembeli transfer PAS sejumlah ini agar mudah dicocokkan.</p>
                        </div>
                      )}
                      {method === "qris" ? (
                        <div className="text-center">
                          <p className="text-xs font-semibold text-[#7A241F] mb-2">Scan QRIS untuk berdana</p>
                          <img src={LOGOS.qris} alt="QRIS" className="mx-auto w-full max-w-[220px] rounded-lg border border-border bg-white" />
                        </div>
                      ) : (
                        <div>
                          <p className="text-xs font-semibold text-[#7A241F] mb-2">Transfer ke rekening</p>
                          <div className="rounded-lg bg-[#F3E9DD]/50 p-3 text-center">
                            <p className="text-xs text-[#7A6A5E]">{transfer?.bank || "BCA"}</p>
                            <p className="font-mono text-xl font-bold text-[#7A241F] tracking-wide">{transfer?.account_number}</p>
                            <p className="text-xs text-[#7A6A5E]">a.n. {transfer?.account_name}</p>
                          </div>
                        </div>
                      )}
                      <p className="text-[11px] text-[#7A6A5E] mt-3 mb-2">Setelah pembeli berdana, foto atau unggah bukti struk:</p>
                      <div className="grid grid-cols-2 gap-2">
                        <label data-testid="preorder-photo" className="flex items-center justify-center gap-2 rounded-lg border border-[#B26A1E]/50 bg-white px-3 py-2.5 text-sm font-medium text-[#8A3A12] cursor-pointer hover:bg-[#F3E9DD]/40">
                          <Camera className="h-4 w-4" /> Ambil Foto
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onProofPreorder} />
                        </label>
                        <label data-testid="preorder-upload" className="flex items-center justify-center gap-2 rounded-lg border border-[#B26A1E]/50 bg-white px-3 py-2.5 text-sm font-medium text-[#8A3A12] cursor-pointer hover:bg-[#F3E9DD]/40">
                          <UploadCloud className="h-4 w-4" /> Upload Struk
                          <input type="file" accept="image/*" className="hidden" onChange={onProofPreorder} />
                        </label>
                      </div>
                      {proof && <img src={proof} alt="bukti" className="rounded-lg border border-border max-h-32 object-contain mt-3 mx-auto" data-testid="preorder-proof-preview" />}
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-lg border border-[#B26A1E]/30 bg-[#F3E9DD]/50 p-3 mb-3" data-testid="preorder-belum-note">
                  <p className="text-sm text-[#7A241F] font-semibold mb-1">Kursi dikunci atas nama pembeli</p>
                  <p className="text-xs text-[#7A6A5E]">Tiket tetap terbit. Nominal kontribusi bebas, sesuai Dana Paramita Anda 🙏. Pembeli menyalurkan Dana Paramita nanti lewat <b>link WhatsApp</b> (pilih QRIS/Transfer, isi nominal, unggah bukti). Panitia verifikasi di panel admin.</p>
                </div>
              )}

              <Button onClick={submitPreorder} disabled={busy || !phone.trim() || selected.length === 0 || !sessionId || (preorderPaid && amount <= 0) || (preorderPaid && !method) || (preorderPaid && (method === "qris" || method === "transfer") && !proof)} data-testid="preorder-submit"
                className="w-full h-12 bg-[#B26A1E] hover:bg-[#8A3A12] text-base">
                {busy ? <Loader2 className="h-5 w-5 animate-spin mr-1.5" /> : <Ticket className="h-5 w-5 mr-1.5" />} {!phone.trim() ? "Isi No HP Dulu" : !preorderPaid ? "Simpan Pre-Order (Belum Berdana)" : !method ? "Pilih Metode Pembayaran" : ((method !== "cash" && !proof) ? "Foto / Upload Bukti Dulu" : "Buat Tiket & Tampilkan E-Ticket")}
              </Button>
            </>
          ) : (
          <>
          <div className="rounded-lg bg-muted/40 p-3 mb-2">
            <p className="text-xs text-[#7A6A5E] mb-1">Kursi dipilih ({selected.length})</p>
            <div className="flex flex-wrap gap-1.5 min-h-[28px]" data-testid="walkin-selected-seats">
              {selected.length === 0 ? <span className="text-xs text-[#9CA3AF]">Belum ada kursi dipilih</span>
                : selected.map((s) => <span key={s} className="px-2.5 py-1 rounded-md bg-[#B26A1E]/10 text-[#8A3A12] text-sm font-bold">{s}</span>)}
            </div>
          </div>
          <div className="rounded-lg border border-[#B26A1E]/30 bg-[#F3E9DD]/50 p-3 mb-3">
            <p className="text-xs font-semibold text-[#7A241F]">Dana Sukarela</p>
            <p className="text-[11px] text-[#7A6A5E]">Acuan {selected.length || 0} tiket × Rp60.000 = <b>{rupiah(refTotal)}</b></p>
            <p className="text-sm font-semibold text-[#7A241F] mt-1.5" data-testid="walkin-free-note">
              Nominal tetap <span className="text-[#B26A1E]">bebas</span> sesuai kerelaan 🙏
            </p>
            <div className="relative mt-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#7A6A5E]">Rp</span>
              <Input data-testid="walkin-amount" inputMode="numeric"
                value={amount ? amount.toLocaleString("id-ID") : ""}
                onChange={(e) => setAmountText(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="0" className="pl-9 h-11 text-base font-semibold bg-white" />
            </div>
          </div>

          <Label>Metode Pembayaran</Label>
          <div className="grid grid-cols-3 gap-2 mt-1.5 mb-2">
            {PAY.map((m) => {
              const Icon = m.icon; const active = method === m.k;
              return (
                <button type="button" key={m.k} data-testid={`walkin-pay-${m.k}`} onClick={() => setMethod(m.k)}
                  className={cn("rounded-xl border p-2.5 text-center transition-colors",
                    active ? "border-[#B26A1E] bg-[#B26A1E]/5 ring-2 ring-[#B26A1E]/30" : "border-border hover:border-[#B26A1E]/50")}>
                  <Icon className={cn("h-5 w-5 mx-auto mb-0.5", active ? "text-[#B26A1E]" : "text-[#7A6A5E]")} />
                  <span className="text-xs font-medium text-[#2C1E16]">{m.t}</span>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-[#7A6A5E] mb-4">{!method ? "Pilih metode pembayaran dulu." : method === "cash" ? "Cash: nominal pas, tanpa kode unik." : "Nominal akan ditambah kode unik otomatis."}</p>

          {method && method !== "cash" && (
            <p className="text-[11px] text-[#7A6A5E] mb-3 rounded-lg bg-[#7A241F]/[0.04] border border-[#7A241F]/10 p-2.5" data-testid="walkin-pay-hint">
              Setelah klik <b>Lanjut Bayar</b>, layar {method === "qris" ? "QRIS" : "rekening transfer"} akan muncul untuk pembeli. Setelah pembeli membayar, foto struk pakai webcam, lalu nomor tiket muncul.
            </p>
          )}

          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-[#7A6A5E]">Total {method === "cash" ? "(pas)" : method ? "(+ kode unik)" : ""}</span>
            <span className="font-serif-display text-2xl text-[#B26A1E]" data-testid="walkin-total">{rupiah(total)}</span>
          </div>

          <Button onClick={startPayment} disabled={busy || selected.length === 0 || !sessionWalkinOpen || !location.trim() || !method} data-testid="walkin-submit"
            className="w-full h-12 bg-[#B26A1E] hover:bg-[#8A3A12] text-base">
            {busy ? <Loader2 className="h-5 w-5 animate-spin mr-1.5" /> : <Ticket className="h-5 w-5 mr-1.5" />} {!method ? "Pilih Metode Pembayaran" : method === "cash" ? "Buat Tiket (LUNAS)" : "Lanjut Bayar"}
          </Button>
          </>
          )}
        </div>
      </div>
      )}

      {/* Payment step dialog (QRIS / Transfer) */}
      <Dialog open={payStep === "pay"} onOpenChange={(o) => { if (!o) cancelPayment(); }}>
        <DialogContent data-testid="walkin-pay-dialog" className="max-w-md rounded-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">
              {method === "qris" ? "Pembayaran QRIS" : "Pembayaran Transfer"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-xl bg-[#B26A1E] text-white p-4 text-center">
              <p className="text-sm text-white/85">Nominal (WAJIB PAS)</p>
              <p className="font-serif-display text-4xl leading-tight" data-testid="walkin-pay-amount">{rupiah(pendingOrder?.total_amount || 0)}</p>
              {pendingOrder?.unique_code ? <p className="text-xs text-white/80 mt-0.5">termasuk kode unik {pendingOrder.unique_code} (untuk cek mutasi)</p> : null}
              <div className="flex items-center justify-center gap-4 mt-2 text-sm">
                <span data-testid="walkin-pay-orderno">No. Tiket: <b>#{pendingOrder?.order_no}</b></span>
                <span data-testid="walkin-pay-qty">{pendingOrder?.seats?.length || pendingOrder?.qty} tiket</span>
              </div>
            </div>

            {method === "qris" ? (
              <div className="rounded-xl border border-[#7A241F]/15 bg-[#7A241F]/[0.03] p-4 text-center">
                <p className="text-sm text-[#7A241F] font-medium mb-2">1️⃣ Minta pembeli scan QRIS ini:</p>
                <img src={LOGOS.qris} alt="QRIS" className="mx-auto w-full max-w-[220px] rounded-lg border border-border bg-white" />
              </div>
            ) : (
              <div className="rounded-xl border border-[#7A241F]/15 bg-[#7A241F]/[0.03] p-4">
                <p className="text-sm text-[#7A241F] font-medium mb-2">1️⃣ Minta pembeli transfer ke:</p>
                <div className="rounded-md bg-white border border-border p-3 space-y-1 text-center">
                  <p className="text-xs text-[#7A6A5E]">{transfer?.bank || "BCA"}</p>
                  <p className="font-mono text-2xl font-bold text-[#7A241F] tracking-wide">{transfer?.account_number}</p>
                  <p className="text-xs text-[#7A6A5E]">a.n. {transfer?.account_name}</p>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-[#B26A1E]/30 bg-[#F3E9DD]/40 p-3">
              <p className="text-sm text-[#8A3A12] font-medium mb-2">2️⃣ Setelah pembeli bayar, foto struk/bukti:</p>
              {proof ? (
                <div className="text-center">
                  <img src={proof} alt="Bukti" className="w-full max-h-40 object-contain rounded-md border border-border bg-white mx-auto" data-testid="walkin-proof-preview" />
                  <button type="button" onClick={() => setProof(null)} data-testid="walkin-proof-remove" className="mt-1.5 text-[11px] text-[#EF4444] underline">Foto ulang</button>
                </div>
              ) : (
                <div className="space-y-2">
                  <button type="button" onClick={openWebcam} data-testid="walkin-proof-webcam"
                    className="flex w-full items-center justify-center gap-2 rounded-md bg-[#7A241F] text-white py-3 font-semibold text-sm hover:bg-[#5E1B17] transition-colors">
                    <Video className="h-5 w-5" /> Foto Struk pakai Webcam
                  </button>
                  <label data-testid="walkin-proof-upload"
                    className="flex items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-[#B26A1E]/40 bg-white py-2.5 cursor-pointer hover:border-[#B26A1E]">
                    {uploadingProof ? <Loader2 className="h-4 w-4 animate-spin text-[#B26A1E]" /> : <UploadCloud className="h-4 w-4 text-[#B26A1E]" />}
                    <span className="text-[11px] font-medium text-[#7A241F]">{uploadingProof ? "Memproses..." : "atau Upload / Pilih File"}</span>
                    <input type="file" accept="image/*" capture="environment" className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingProof(true);
                        try { setProof(await compressImage(file)); }
                        catch { toast.error("Gagal memproses foto"); }
                        setUploadingProof(false);
                      }} />
                  </label>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={cancelPayment} disabled={busy} className="flex-1">Batal</Button>
            <Button onClick={confirmPayment} disabled={busy || !proof} data-testid="walkin-pay-confirm" className="flex-1 bg-[#2F703E] hover:bg-[#255E33]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Konfirmasi LUNAS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Webcam capture dialog */}
      <Dialog open={webcamOpen} onOpenChange={(o) => { if (!o) stopWebcam(); }}>
        <DialogContent data-testid="walkin-webcam-dialog" className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl text-[#7A241F] flex items-center gap-2"><Video className="h-5 w-5" /> Foto Struk pakai Webcam</DialogTitle>
          </DialogHeader>
          <div className="rounded-xl overflow-hidden bg-black">
            <video ref={videoRef} playsInline muted className="w-full h-auto max-h-[50vh] object-contain" data-testid="walkin-webcam-video" />
          </div>
          <p className="text-xs text-[#7A6A5E]">Arahkan struk/bukti ke webcam, pastikan jelas terbaca, lalu tekan Jepret.</p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={stopWebcam} className="flex-1">Batal</Button>
            <Button onClick={snapWebcam} data-testid="walkin-webcam-snap" className="flex-1 bg-[#B26A1E] hover:bg-[#8A3A12]"><Camera className="h-4 w-4 mr-1.5" /> Jepret</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result popup */}
      <Dialog open={!!result} onOpenChange={(o) => { if (!o) finishSaleToMenu(); }}>
        <DialogContent data-testid="walkin-result-dialog" className="max-w-lg rounded-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-[#2F703E]/15 flex items-center justify-center shrink-0"><CheckCircle2 className="h-6 w-6 text-[#2F703E]" /></div>
              <div>
                <DialogTitle className="font-serif-display text-3xl text-[#2F703E]">{result?.preorder ? (result?.paid ? "PRE-ORDER · SUDAH BERDANA" : "PRE-ORDER TERSIMPAN") : "LUNAS ✅"}</DialogTitle>
                {result && <p className="text-sm text-[#7A6A5E] mt-0.5"><b className="text-[#2C1E16]">{result.name}</b> · {(result.preorder && !result.paid) ? "Belum Berdana" : rupiah(result.total_amount)}</p>}
              </div>
            </div>
          </DialogHeader>
          {result && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-[#7A241F]/[0.05] border border-[#7A241F]/10 p-4 text-center">
                  <p className="text-xs text-[#7A6A5E] mb-0.5">Nomor Tiket</p>
                  <p className="font-mono text-3xl font-bold text-[#7A241F]" data-testid="walkin-result-orderno">#{result.order_no}</p>
                </div>
                <div className="rounded-lg bg-[#B26A1E]/10 border border-[#B26A1E]/20 p-4 text-center">
                  <p className="text-xs text-[#7A6A5E] mb-0.5">Jumlah Tiket</p>
                  <p className="font-serif-display text-3xl text-[#B26A1E]" data-testid="walkin-result-qty">{result.seats.length} tiket</p>
                </div>
              </div>
              {result.preorder ? (
                <div className="rounded-lg bg-[#B26A1E]/[0.08] border border-[#B26A1E]/30 p-4">
                  <p className="text-[#8A3A12] font-bold mb-1">📌 PRE-ORDER — tiket belum diserahkan</p>
                  <p className="text-xs text-[#7A6A5E] mb-3">Peserta <b>WAJIB check-in & ambil tiket fisik pada Minggu, 13 Sep 2026</b> di CGV Grand Batam. Kirim e-ticket sebagai bukti pre-order.</p>
                  <p className="font-serif-display text-xl text-[#7A241F]" data-testid="walkin-result-session">
                    {(SESSIONS.find((s) => s.id === result.session_id)?.name || `Sesi ${result.session_id}`).toUpperCase()} · {SESSIONS.find((s) => s.id === result.session_id)?.time}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="walkin-result-seats">
                    {result.seats.map((s) => (<span key={s} className="px-3 py-1.5 rounded-md bg-white text-[#8A3A12] font-bold text-lg border border-[#B26A1E]/30">{s}</span>))}
                  </div>
                </div>
              ) : (
              <div className="rounded-lg bg-[#7A241F]/[0.04] border border-[#7A241F]/10 p-4">
                <p className="text-[#8A3A12] font-medium mb-2">🎟️ Serahkan tiket:</p>
                <p className="font-serif-display text-2xl text-[#7A241F] mb-2" data-testid="walkin-result-session">
                  {(SESSIONS.find((s) => s.id === result.session_id)?.name || `Sesi ${result.session_id}`).toUpperCase()} · {SESSIONS.find((s) => s.id === result.session_id)?.time}
                </p>
                <p className="text-xs text-[#7A6A5E] mb-1">Nomor kursi:</p>
                <div className="flex flex-wrap gap-2" data-testid="walkin-result-seats">
                  {result.seats.map((s) => (
                    <span key={s} className="px-3 py-1.5 rounded-md bg-white text-[#8A3A12] font-bold text-lg border border-[#B26A1E]/30">{s}</span>
                  ))}
                </div>
              </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            {result?.phone ? (
              <Button onClick={() => {
                let p = (result.phone || "").replace(/[^0-9]/g, "");
                if (p.startsWith("0")) p = "62" + p.slice(1); else if (!p.startsWith("62")) p = "62" + p;
                const sName = SESSIONS.find((s) => s.id === result.session_id)?.name || `Sesi ${result.session_id}`;
                const sTime = SESSIONS.find((s) => s.id === result.session_id)?.time || "";
                const link = `${window.location.origin}/order/${result.id}`;
                let msg;
                if (!result.preorder) {
                  msg = `Namo Buddhaya, ${result.name}\n\nTerima kasih. Berikut E-TICKET Film Dokumenter ASHIN JINARAKKHITA:\nNo. Order: #${result.order_no}\nSesi: ${sName} - ${sTime}\nKursi: ${result.seats.join(", ")}\nJumlah: ${result.seats.length} tiket\nDana: ${rupiah(result.total_amount)}\n\nLihat & simpan e-ticket:\n${link}\n\nTunjukkan e-ticket ini saat check-in di lokasi.\n- Sekretariat MBI Kepri`;
                } else if (result.paid) {
                  msg = `Namo Buddhaya, ${result.name}\n\nTerima kasih atas Dana Paramita Anda untuk Film Dokumenter ASHIN JINARAKKHITA.\n\nBerikut E-TICKET pre-order Anda:\nNama: ${result.name}\nNo HP: ${result.phone}\nNo. Order: #${result.order_no}\nSesi: ${sName} - ${sTime}\nKursi: ${result.seats.join(", ")}\nJumlah: ${result.seats.length} tiket\nDana Paramita: ${rupiah(result.total_amount)}\n\nLihat & simpan e-ticket:\n${link}\n\nMohon hadir untuk CHECK-IN & menukar tiket fisik pada Minggu, 13 September 2026 di CGV Grand Batam Mall.\n\nSadhu, Sadhu, Sadhu.\n- Sekretariat MBI Kepri`;
                } else {
                  msg = `Namo Buddhaya, ${result.name}\n\nTerima kasih. Tempat duduk Anda untuk Film Dokumenter ASHIN JINARAKKHITA sudah kami amankan (pre-order):\nNama: ${result.name}\nNo HP: ${result.phone}\nNo. Order: #${result.order_no}\nSesi: ${sName} - ${sTime}\nKursi: ${result.seats.join(", ")}\nJumlah: ${result.seats.length} tiket\n\nApabila Anda berkenan menyalurkan Dana Paramita, silakan klik tautan berikut:\n${link}\n\nNominal kontribusi bebas, sesuai Dana Paramita Anda. Di halaman tersebut Anda bisa pilih QRIS/Transfer, isi nominal, lalu unggah bukti - sekaligus melihat & menyimpan e-ticket.\n\nMohon hadir untuk CHECK-IN & menukar tiket fisik pada Minggu, 13 September 2026 di CGV Grand Batam Mall.\n\nSadhu, Sadhu, Sadhu.\n- Sekretariat MBI Kepri`;
                }
                window.open(`https://wa.me/${p}?text=${encodeURIComponent(msg)}`, "_blank");
              }} data-testid="walkin-result-wa" className="w-full h-11 bg-[#25D366] hover:bg-[#1EBE5A] text-white">
                <Send className="h-4 w-4 mr-1.5" /> Kirim E-Ticket ke WhatsApp
              </Button>
            ) : null}
            <Button onClick={() => saveTicketImage(result)} data-testid="walkin-result-download" className="w-full h-11 bg-[#B26A1E] hover:bg-[#8A3A12] text-white">
              <Ticket className="h-4 w-4 mr-1.5" /> Download E-Ticket
            </Button>
            <Button onClick={finishSaleToMenu} variant="outline" className="w-full h-11" data-testid="walkin-result-ok">{result?.preorder ? "Selesai" : "Sudah Saya Serahkan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
